import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
//import 'bootstrap/dist/css/bootstrap.min.css';

const API_URL = process.env.REACT_APP_API_URL;
const GET_EXAM_PERIODS_URL = `${API_URL}/Custom/GetExamPeriods`;
const GET_TEACHERS_URL = `${API_URL}/Custom/GetAssignableUsers`;
const GET_ASSIGNMENT_ROLES_URL = `${API_URL}/Custom/GetAssignmentRoles`;
const GET_TIMESLOTS_URL = `${API_URL}/Custom/GetTimeSlots`;
const GET_UNASSIGNED_COUNT_URL = `${API_URL}/Custom/GetUnassignedSlotClassroomCount`;
const POST_ASSIGN_URL = `${API_URL}/Custom/AssignTeaching`;
const RESET_ASSIGNMENTS_URL = `${API_URL}/Custom/ResetTeachingAssignments`;

export default function TeachingAssignmentComponent() {
    const [step, setStep] = useState(1);
    const [examPeriods, setExamPeriods] = useState([]);
    const [selectedExamPeriod, setSelectedExamPeriod] = useState(null);

    // 1) Eskiden ayrı ayrı tutulan head/inv değişkenlerini tek array içinde toplayacağız:
    const [unassignedMessages, setUnassignedMessages] = useState([]);

    const [staff, setStaff] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState([]);

    const [timeSlots, setTimeSlots] = useState([]);
    const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);

    const [roles, setRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);

    const [status, setStatus] = useState('');
    const [isResetting, setIsResetting] = useState(false);

    // Exam dönemleri, öğretmenler ve roller i̇lk yükleme
    useEffect(() => {
        axios.get(GET_EXAM_PERIODS_URL)
            .then(res => setExamPeriods(res.data || []))
            .catch(console.error);
        axios.get(GET_TEACHERS_URL)
            .then(res => setStaff(res.data || []))
            .catch(console.error);
        axios.get(GET_ASSIGNMENT_ROLES_URL)
            .then(res => setRoles(res.data || []))
            .catch(console.error);
    }, []);

    // selectedExamPeriod değişince unassignedMessages’ı güncelle
    useEffect(() => {
        if (!selectedExamPeriod) {
            setUnassignedMessages([]);
            return;
        }
        fetchUnassigned(selectedExamPeriod);
    }, [selectedExamPeriod]);

    const fetchUnassigned = periodId => {
        axios.get(`${GET_UNASSIGNED_COUNT_URL}?examPeriodId=${periodId}`)
            .then(res => {
                // Artık API; ["Sınıf A101 için ... eksik.", "Sınıf B203 için ... eksik.", ...] formatında dizi döndürüyor
                setUnassignedMessages(res.data || []);
            })
            .catch(() => {
                setUnassignedMessages([]);
            });
    };

    useEffect(() => {
        if (step !== 3 || !selectedExamPeriod) return;
        axios.get(`${GET_TIMESLOTS_URL}?examPeriodId=${selectedExamPeriod}`)
            .then(res => setTimeSlots(res.data || []))
            .catch(console.error);
    }, [step, selectedExamPeriod]);

    const next = () => setStep(s => Math.min(s + 1, 5));
    const prev = () => setStep(s => Math.max(s - 1, 1));

    const handleSubmit = async () => {
        setStatus('Görevlendiriliyor...');
        try {
            await axios.post(POST_ASSIGN_URL, {
                ApplicationUserIds: selectedStaff,
                TimeSlotIds: selectedTimeSlots,
                AssignmentRoleIds: selectedRoles
            });
            setStatus('Sınav görevleri başarıyla atandı!');
            // seçimleri sıfırla
            setSelectedStaff([]);
            setSelectedTimeSlots([]);
            setSelectedRoles([]);
            // adım 1’e dön
            setStep(1);
            // kalan atama mesajlarını yeniden çek
            fetchUnassigned(selectedExamPeriod);
        } catch (err) {
            setStatus('Hata: ' + (err.response?.data || err.message));
        }
    };

    const handleResetAssignments = async () => {
        if (!selectedExamPeriod || isResetting) return;

        const confirmReset = window.confirm('Seçilen döneme ait tüm görevli atamaları silinecek. Devam etmek istiyor musunuz?');
        if (!confirmReset) {
            return;
        }

        setIsResetting(true);
        setStatus('Görevler sıfırlanıyor...');
        try {
            const response = await axios.post(RESET_ASSIGNMENTS_URL, { examPeriodId: selectedExamPeriod });
            const message = response.data?.message || 'Görevler sıfırlandı.';
            setStatus(message);
            fetchUnassigned(selectedExamPeriod);
        } catch (err) {
            const message = err.response?.data?.message || err.response?.data || err.message;
            setStatus('Hata: ' + message);
        } finally {
            setIsResetting(false);
        }
    };

    // Step 2 için toplu seçim başlıkları
    const titles = useMemo(() => Array.from(new Set(staff.map(u => u.title))).filter(x => x), [staff]);
    const selectByTitle = title => {
        const ids = staff.filter(u => u.title === title).map(u => u.id);
        setSelectedStaff(prev => Array.from(new Set([...prev, ...ids])));
    };
    const clearByTitle = title => {
        setSelectedStaff(prev => prev.filter(id => staff.find(u => u.id === id).title !== title));
    };

    return (
        <div className="container mt-4">
            <h2 className="mb-4">Öğretim Elemanı Atama</h2>
            <ul className="nav nav-pills mb-4">
                {['Dönem Seçimi', 'Eleman Seçimi', 'Slot Seçimi', 'Rol Seçimi', 'Onay'].map((lbl, i) => (
                    <li className="nav-item" key={i}>
                        <span className={`nav-link ${step === i + 1 ? 'active' : ''}`}>
                            {i + 1}. {lbl}
                        </span>
                    </li>
                ))}
            </ul>
            <div className="card p-4">
                <div className="mb-4 d-flex justify-content-between">
                    <button className="btn btn-secondary" onClick={prev} disabled={step === 1}>Geri</button>
                    <button className="btn btn-secondary" onClick={next} disabled={
                        (step === 1 && !selectedExamPeriod) ||
                        (step === 2 && selectedStaff.length === 0) ||
                        (step === 3 && selectedTimeSlots.length === 0) ||
                        (step === 4 && selectedRoles.length === 0) ||
                        step === 5
                    }>
                        {step < 5 ? 'İleri' : ''}
                    </button>
                </div>

                {step === 1 && (
                    <>
                        <h5>1. Dönem Seçimi</h5>
                        <select
                            className="form-control"
                            value={selectedExamPeriod || ''}
                            onChange={e => setSelectedExamPeriod(Number(e.target.value))}
                        >
                            <option value="" disabled>Seçiniz</option>
                            {examPeriods.map(ep =>
                                <option key={ep.id} value={ep.id}>{ep.name}</option>
                            )}
                        </select>

                        {/*
                            1. Adımda artık unassignedHead/unassignedInvigilator yok.
                            Bunun yerine API’den gelen string mesaj listesini gösteriyoruz.
                        */}
                        {unassignedMessages.length > 0 && (
                            <div className="mt-3">
                                <small>Ataması Eksik Olan Bileşenler:</small>
                                <ul className="mt-1">
                                    {unassignedMessages.map((msg, idx) => (
                                        <li key={idx}>{msg}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="mt-3">
                            <button
                                className="btn btn-danger"
                                onClick={handleResetAssignments}
                                disabled={!selectedExamPeriod || isResetting}
                            >
                                {isResetting ? 'Sıfırlanıyor...' : 'Görevli Atamalarını Sıfırla'}
                            </button>
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        <h5>2. Görevlendirilecek Elemanları Seç</h5>
                        <div className="mb-3">
                            <small>Toplu Seçim: </small>
                            {titles.map(t => (
                                <span key={t} className="mr-2">
                                    <button
                                        className="btn btn-sm btn-outline-primary mr-1"
                                        onClick={() => selectByTitle(t)}>
                                        Tüm {t}
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => clearByTitle(t)}>
                                        {t} Temizle
                                    </button>
                                </span>
                            ))}
                        </div>
                        {staff.map(u => (
                            <div className="form-check" key={u.id}>
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedStaff.includes(u.id)}
                                    onChange={() =>
                                        setSelectedStaff(prev =>
                                            prev.includes(u.id)
                                                ? prev.filter(x => x !== u.id)
                                                : [...prev, u.id]
                                        )
                                    }
                                />
                                <label className="form-check-label ml-2">
                                    {u.name} <em>({u.title})</em>
                                </label>
                            </div>
                        ))}
                    </>
                )}

                {step === 3 && (
                    <>
                        <h5>3. Slot Seçimi</h5>
                        <div className="mb-2">
                            <button
                                className="btn m-1"
                                style={{
                                    border: '1px solid #007bff',
                                    backgroundColor: selectedTimeSlots.length === timeSlots.length ? '#007bff' : 'transparent',
                                    color: selectedTimeSlots.length === timeSlots.length ? '#fff' : '#007bff'
                                }}
                                onClick={() =>
                                    setSelectedTimeSlots(
                                        selectedTimeSlots.length === timeSlots.length
                                            ? []
                                            : timeSlots.map(ts => ts.id)
                                    )
                                }
                            >
                                {selectedTimeSlots.length === timeSlots.length ? 'Temizle' : 'Hepsini Seç'}
                            </button>
                        </div>
                        {Array.from(new Set(timeSlots.map(ts => ts.exactDate))).sort().map(date => {
                            const ids = timeSlots.filter(ts => ts.exactDate === date).map(ts => ts.id);
                            const allSel = ids.every(id => selectedTimeSlots.includes(id));
                            return (
                                <div key={date} className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h6>
                                            {new Date(date).toLocaleDateString('tr-TR', {
                                                weekday: 'long',
                                                year: 'numeric',
                                                month: 'numeric',
                                                day: 'numeric'
                                            })}
                                        </h6>
                                        <button
                                            className="btn m-1"
                                            style={{
                                                border: '1px solid #007bff',
                                                backgroundColor: allSel ? '#007bff' : 'transparent',
                                                color: allSel ? '#fff' : '#007bff'
                                            }}
                                            onClick={() =>
                                                setSelectedTimeSlots(prev =>
                                                    allSel
                                                        ? prev.filter(x => !ids.includes(x))
                                                        : [...new Set([...prev, ...ids])]
                                                )
                                            }
                                        >
                                            {allSel ? 'Kaldır' : 'Seç'}
                                        </button>
                                    </div>
                                    <div className="d-flex flex-wrap">
                                        {ids.map(id => {
                                            const ts = timeSlots.find(x => x.id === id);
                                            const sel = selectedTimeSlots.includes(id);
                                            return (
                                                <button
                                                    key={id}
                                                    className="btn m-1"
                                                    style={{
                                                        border: '1px solid #007bff',
                                                        backgroundColor: sel ? '#007bff' : 'transparent',
                                                        color: sel ? '#fff' : '#007bff'
                                                    }}
                                                    onClick={() =>
                                                        setSelectedTimeSlots(prev =>
                                                            sel
                                                                ? prev.filter(x => x !== id)
                                                                : [...prev, id]
                                                        )
                                                    }
                                                >
                                                    {`${ts.startTime.substring(0, 5)} - ${ts.endTime.substring(0, 5)}`}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p>Seçilen Slot: {selectedTimeSlots.length}</p>
                                </div>
                            );
                        })}
                    </>
                )}

                {step === 4 && (
                    <>
                        <h5>4. Atama Rolleri Seç</h5>
                        {roles.map(r => (
                            <div className="form-check" key={r.id}>
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedRoles.includes(r.id)}
                                    onChange={() =>
                                        setSelectedRoles(prev =>
                                            prev.includes(r.id)
                                                ? prev.filter(x => x !== r.id)
                                                : [...prev, r.id]
                                        )
                                    }
                                />
                                <label className="form-check-label ml-2">{r.name}</label>
                            </div>
                        ))}
                    </>
                )}

                {step === 5 && (
                    <>
                        <h5>5. Onay & Gönder</h5>
                        <p>
                            <strong>Dönem:</strong>{' '}
                            {examPeriods.find(ep => ep.id === selectedExamPeriod)?.name}
                        </p>
                        <p><strong>Seçilen Eleman:</strong> {selectedStaff.length}</p>
                        <p><strong>Seçilen Slot:</strong> {selectedTimeSlots.length}</p>
                        <p>
                            <strong>Seçilen Rol:</strong>{' '}
                            {selectedRoles.map(id => roles.find(r => r.id === id)?.name).join(', ')}
                        </p>
                        <button className="btn btn-primary mt-2" onClick={handleSubmit}>
                            Atamayı Gerçekleştir
                        </button>
                    </>
                )}

                {status && <div className="alert alert-info mt-3">{status}</div>}

                <div className="mt-4 d-flex justify-content-between">
                    <button className="btn btn-secondary" onClick={prev} disabled={step === 1}>
                        Geri
                    </button>
                    <button className="btn btn-secondary" onClick={next} disabled={step === 5}>
                        {step < 5 ? 'İleri' : ''}
                    </button>
                </div>
            </div>
        </div>
    );
}
